import { useReducer } from "react";
import { candidateViewReducer } from "../viewModel/canidate.reduser";
import { initialCandidateViewState } from "../viewModel/candidate.action";
import {
  Certification,
  Experience,
  Language,
  Skill,
  Education,
} from "../../company/JobManagement/model/JobModel";
import { format } from "date-fns";

interface CandidateViewDetailsProps {
  candidate: any;
  contactUnlocked: boolean;
  onViewContact: () => void;
}

const formatPhoneWithCountryCode = (countryCode?: string, phone?: string) => {
  const cleanPhone = phone?.trim();
  const cleanCountryCode = countryCode?.trim();

  if (!cleanPhone) return "";
  if (!cleanCountryCode || cleanPhone.startsWith("+")) return cleanPhone;

  return `${cleanCountryCode} ${cleanPhone}`;
};

export const CandidateViewDetails = ({
  candidate,
  contactUnlocked,
  onViewContact,
}: CandidateViewDetailsProps) => {
  const [state, dispatch] = useReducer(
    candidateViewReducer,
    initialCandidateViewState,
  );
  const tabs = [
    { id: "overview", name: "Overview", icon: "👤" },
    { id: "experience", name: "Experience", icon: "💼" },
    { id: "education", name: "Education", icon: "🎓" },
    { id: "skills", name: "Skills", icon: "⚡" },
    { id: "projects", name: "Projects", icon: "🚀" },
    { id: "certifications", name: "Certifications", icon: "🏆" },
    { id: "additional", name: "Languages & Awards", icon: "🌟" },
    // { id: "resumes", name: "Resumes", icon: "📄" },
  ];
  const formatMonthYear = (date?: string) => {
    if (!date) return "";
    return format(new Date(date), "MMM yyyy");
  };
  const displayPhone = formatPhoneWithCountryCode(
    candidate?.country_code,
    candidate?.phone,
  );
  return (
    <div className="w-full bg-white rounded-lg shadow-sm border flex flex-col min-h-0">
      <div className="border-b border-gray-200">
        <nav className="overflow-x-auto border-b">
          <div className="flex gap-4 px-4 sm:px-6 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() =>
                  dispatch({
                    type: "SET_ACTIVE_TAB",
                    payload: tab.id as any,
                  })
                }
                className={`py-3 px-2 border-b-2 whitespace-nowrap text-sm font-medium transition-colors
          ${
            state.activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
              >
                <span className="mr-1">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
        {state.activeTab === "overview" && (
          <div className="relative">
            <div>
              <div className="space-y-6 ">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Contact Information
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Current Job Title
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.current_job_title || "No Title"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Current Company:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.current_company || "Not Provided"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Email:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.email}
                        </dd>
                      </div>
                      {displayPhone && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Phone:
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {displayPhone}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Full Name:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.full_name}
                        </dd>
                      </div>

                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Location:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.location || "Not Provided"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Experience:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.years_of_experience} years
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Ai Career level:
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.ai_career_level || "Not Provided"}
                        </dd>
                      </div>
                      {candidate.linkedin_url && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            LinkedIn
                          </dt>
                          <dd className="text-sm text-gray-900">
                            <a
                              href={candidate.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-500"
                            >
                              View Profile
                            </a>
                          </dd>
                        </div>
                      )}

                      {candidate.github_url && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            GitHub
                          </dt>
                          <dd className="text-sm text-gray-900">
                            <a
                              href={candidate.github_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-500"
                            >
                              View Profile
                            </a>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Professional Summary
                    </h3>
                    {candidate.professional_summary ? (
                      <p className="text-sm text-gray-700 leading-relaxed break-words">
                        {candidate.professional_summary}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        No summary available
                      </p>
                    )}

                    {candidate.expected_salary && (
                      <div className="mt-4">
                        <dt className="text-sm font-medium text-gray-500">
                          Expected Salary
                        </dt>
                        <dd className="text-sm text-gray-900">
                          ${candidate.expected_salary.toLocaleString()}
                        </dd>
                      </div>
                    )}
                    {candidate.preferred_work_type && (
                      <div className="mt-4">
                        <dt className="text-sm font-medium text-gray-500">
                          Preferred Work Type
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.preferred_work_type}
                        </dd>
                      </div>
                    )}
                    {candidate.work_authorization && (
                      <div className="mt-4">
                        <dt className="text-sm font-medium text-gray-500">
                          Work Authorization
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {candidate.work_authorization}
                        </dd>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {!contactUnlocked && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-md p-4">
                <button
                  onClick={onViewContact}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md shadow hover:bg-indigo-700 "
                >
                  View Contact Details
                </button>
              </div>
            )}
          </div>
        )}

        {state.activeTab === "experience" && (
          <div className="max-h-[65vh] sm:max-h-none overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Work Experience
            </h3>

            {candidate?.experience && candidate.experience.length > 0 ? (
              <div className="space-y-6">
                {[...candidate.experience]
                  .sort((a, b) => {
                    if (a.is_current) return -1;
                    if (b.is_current) return 1;
                    return (
                      new Date(b.start_date).getTime() -
                      new Date(a.start_date).getTime()
                    );
                  })
                  .map((exp: Experience, index: number) => (
                    <div
                      key={index}
                      className="border-l-4 border-indigo-200 pl-6 pb-6"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {exp.job_title}
                          </h4>

                          <p className="text-md text-indigo-600 font-medium">
                            {exp.company_name}
                          </p>

                          <p className="text-sm text-gray-500 mt-1">
                            {exp.start_date && formatMonthYear(exp.start_date)}{" "}
                            {"-"}
                            {exp.is_current
                              ? " Present"
                              : exp.end_date &&
                                ` ${formatMonthYear(exp.end_date)}`}
                            {exp.location && ` • ${exp.location}`}
                          </p>

                          {exp.employment_type && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-2">
                              {exp.employment_type
                                .replace("_", " ")
                                .toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>

                      {exp.description && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-900 mb-2">
                            Description:
                          </p>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {exp.description}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">💼</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Work Experience
                </h3>
                <p className="text-gray-500">
                  No work experience information available for this candidate.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Education Tab */}
        {state.activeTab === "education" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Education
            </h3>
            {candidate.education && candidate.education.length > 0 ? (
              <div className="space-y-6">
                {candidate.education.map((edu: Education, index: number) => (
                  <div
                    key={index}
                    className="border-l-4 border-green-200 pl-6 pb-6"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {edu.field_of_study ||
                            `${edu.field_of_study} in ${edu.field_of_study}`}
                        </h4>
                        <p className="text-lg text-green-600 font-medium">
                          {edu.institution_name}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {edu.graduation_year &&
                            `Graduated: ${edu.graduation_year}`}
                        </p>

                        <p className="text-sm text-gray-600 mt-1">
                          Graduation Date: {edu.graduation_date || "--"}
                        </p>
                      </div>
                      <div className="text-right">
                        <div>
                          <p className="text-xs text-gray-500">GPA</p>
                          <p className="text-sm font-medium text-green-600">
                            {edu.gpa || "--"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🎓</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Education Information
                </h3>
                <p className="text-gray-500">
                  No education information available for this candidate.
                </p>
              </div>
            )}
          </div>
        )}
        {/* Skills Tab */}
        {state.activeTab === "skills" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Technical & Professional Skills
            </h3>
            {candidate.skills && candidate.skills.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {candidate.skills.map((skill: Skill, index: number) => (
                  <div
                    key={index}
                    className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-indigo-900">
                        {skill.skill_name}
                      </h4>
                      {/* {skill.confidence_score && (
                        <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded">
                          {Math.round(skill.confidence_score * 100)}%
                        </span>
                      )} */}
                    </div>
                    <div className="space-y-1">
                      {skill.proficiency_level && (
                        <p className="text-xs text-indigo-700">
                          <span className="font-medium">Level:</span>{" "}
                          {skill.proficiency_level}
                        </p>
                      )}
                      {skill.years_of_experience && (
                        <p className="text-xs text-indigo-700">
                          <span className="font-medium">Experience:</span>{" "}
                          {skill.years_of_experience} years
                        </p>
                      )}
                      {/* {skill.context && (
                        <p className="text-xs text-indigo-600">
                          {skill.context}
                        </p>
                      )} */}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">⚡</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Skills Listed
                </h3>
                <p className="text-gray-500">
                  No technical or professional skills information available for
                  this candidate.
                </p>
              </div>
            )}
          </div>
        )}
        {/* Projects Tab */}
        {/* {state.activeTab === "projects" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Projects & Portfolio
            </h3>
            {candidate.projects && candidate.projects.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {candidate.projects.map((project:Proje, index) => (
                  <div
                    key={index}
                    className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {typeof project === "string" ? project : project.name}
                      </h4>
                      {typeof project === "object" && project.url && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-500"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      )}
                    </div>

                    {typeof project === "object" && (
                      <>
                        {project.description && (
                          <p className="text-sm text-gray-700 mb-3">
                            {project.description}
                          </p>
                        )}

                        {project.role && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Role:</span>{" "}
                            {project.role}
                          </p>
                        )}

                        {project.technologies &&
                          Array.isArray(project.technologies) &&
                          project.technologies.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">
                                Technologies:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {project.technologies.map((tech, techIndex) => (
                                  <span
                                    key={techIndex}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                        {project.achievements &&
                          Array.isArray(project.achievements) &&
                          project.achievements.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">
                                Key Achievements:
                              </p>
                              <ul className="list-disc list-inside space-y-1">
                                {project.achievements.map(
                                  (achievement, achIndex) => (
                                    <li
                                      key={achIndex}
                                      className="text-sm text-gray-600"
                                    >
                                      {achievement}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                        {(project.start_date || project.end_date) && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              {project.start_date &&
                                new Date(project.start_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                  },
                                )}
                              {project.start_date &&
                                (project.end_date || project.is_ongoing) &&
                                " - "}
                              {project.is_ongoing
                                ? "Ongoing"
                                : project.end_date
                                ? new Date(project.end_date).toLocaleDateString(
                                    "en-US",
                                    {
                                      year: "numeric",
                                      month: "short",
                                    },
                                  )
                                : ""}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🚀</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Projects Listed
                </h3>
                <p className="text-gray-500">
                  No project or portfolio information available for this
                  candidate.
                </p>
              </div>
            )}
          </div>
        )} */}
        {state.activeTab === "projects" && (
          <>
            {candidate.projects &&
              Array.isArray(candidate.projects) &&
              candidate.projects.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Projects
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {candidate.projects.map((project: any, index: number) => (
                      <div
                        key={index}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          {typeof project === "string"
                            ? project
                            : project.name || project.title}
                        </h4>
                        {typeof project === "object" && (
                          <>
                            {project.description && (
                              <p className="text-sm text-gray-700 mb-2">
                                {project.description}
                              </p>
                            )}
                            {project.technologies &&
                              Array.isArray(project.technologies) && (
                                <div className="flex flex-wrap gap-1">
                                  {project.technologies.map(
                                    (tech: string, i: number) => (
                                      <span
                                        key={i}
                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800"
                                      >
                                        {tech}
                                      </span>
                                    ),
                                  )}
                                </div>
                              )}
                            {project.url && (
                              <a
                                href={project.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-indigo-600 hover:text-indigo-500 mt-2 inline-block"
                              >
                                View Project →
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </>
        )}

        {/* Certifications Tab */}
        {state.activeTab === "certifications" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Professional Certifications
            </h3>
            {candidate.certifications && candidate.certifications.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {candidate.certifications.map(
                  (cert: Certification, index: number) => (
                    <div
                      key={index}
                      className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-green-900">
                            {typeof cert === "string" ? cert : cert.name}
                          </h4>
                          {typeof cert === "object" &&
                            cert.issuing_organization && (
                              <p className="text-sm text-green-700 font-medium mt-1">
                                {cert.issuing_organization}
                              </p>
                            )}
                        </div>
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                          />
                        </svg>
                      </div>

                      {typeof cert === "object" && (
                        <div className="space-y-2">
                          {cert.issue_date && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Issued:</span>{" "}
                              {new Date(cert.issue_date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                },
                              )}
                            </p>
                          )}

                          {cert.expiration_date && !cert.does_not_expire && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Expires:</span>{" "}
                              {new Date(
                                cert.expiration_date,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                              })}
                            </p>
                          )}

                          {cert.does_not_expire && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Valid:</span> Does
                              not expire
                            </p>
                          )}

                          {cert.credential_id && (
                            <p className="text-sm text-green-600">
                              <span className="font-medium">ID:</span>{" "}
                              {cert.credential_id}
                            </p>
                          )}

                          {cert.credential_url && (
                            <a
                              href={cert.credential_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-green-600 hover:text-green-500 mt-2"
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              Verify Credential
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🏆</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Certifications Listed
                </h3>
                <p className="text-gray-500">
                  No professional certifications available for this candidate.
                </p>
              </div>
            )}
          </div>
        )}
        {/* Additional Tab - Languages & Awards */}
        {state.activeTab === "additional" && (
          <div className="space-y-8">
            {/* Languages Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Languages
              </h3>
              {candidate.languages && candidate.languages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {candidate.languages.map((lang: Language, index: number) => (
                    <div
                      key={index}
                      className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-blue-900">
                          {typeof lang === "string" ? lang : lang.name}
                        </h4>
                        {typeof lang === "object" && lang.is_native && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Native
                          </span>
                        )}
                      </div>
                      {typeof lang === "object" && lang.proficiency && (
                        <p className="text-sm text-blue-700 mt-1 capitalize">
                          {lang.proficiency} proficiency
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
                  <svg
                    className="mx-auto h-8 w-8 text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-blue-600">
                    No languages listed
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
